import { API_PREFIX } from "../constants";
import { logger } from "./logger";
import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  TokenRefreshRequest,
  TokenRefreshResponse,
  UserProfileResponse,
} from "../types/auth";
import type {
  FactoryGame,
  ContentCollection,
  ContentSchema,
  ValidationReport,
  SaveCollectionResponse,
} from "../types/factory";
import type {
  CreateRoomRequest,
  CreateRoomResponse,
  JoinRoomRequest,
  JoinRoomResponse,
  RoomListResponse,
  RoomLookupResponse,
  CardsListResponse,
  GamesResponse,
  AdminStatsResponse,
  AdminSessionsResponse,
  AdminSessionDetailResponse,
  AdminDeleteResponse,
  AdminFlushResponse,
  AdminBroadcastResponse,
  AdminUsersResponse,
  AdminUserInfo,
  AdminUserUpdateRequest,
  AdminUserAuditHistoryResponse,
  AdminPasswordResetResponse,
  AdminUserDeleteResponse,
  MySessionResponse,
  LeaveSessionResponse,
  ClaimNpcResponse,
  AdminInviteListResponse,
  AdminInviteUsageResponse,
  AdminCreateInviteRequest,
  AdminCreateInviteResponse,
  AdminRevokeInviteResponse,
  TestmodeStatusResponse,
  ModeSettingsResponse,
  ModeToggleResponse,
} from "../types/game";
// Use relative URL with /api prefix (same origin, no CORS issues)
const API_BASE_URL = import.meta.env.VITE_API_URL || API_PREFIX;

/** Configuration for API request retry logic */
interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  retryableStatuses: number[];
}

/** Default retry configuration */
const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 10000,
  retryableStatuses: [408, 429, 500, 502, 503, 504],
};

class ApiClient {
  private baseUrl: string;
  private tokenGetter: (() => string | null) | null = null;
  private onUnauthorized: (() => void) | null = null;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /** Set a function to retrieve the current access token */
  setTokenGetter(getter: () => string | null) {
    this.tokenGetter = getter;
  }

  /** Set a callback to handle 401 Unauthorized responses (e.g., clear auth state) */
  setUnauthorizedHandler(handler: () => void) {
    this.onUnauthorized = handler;
  }

  private async request<T>(
    endpoint: string,
    options?: RequestInit & { skipAuth?: boolean },
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    logger.debug("api_request", { method: options?.method || "GET", endpoint });

    // Auto-inject Bearer token if available and not explicitly skipped
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...((options?.headers as Record<string, string>) || {}),
    };

    if (!options?.skipAuth && this.tokenGetter) {
      const token = this.tokenGetter();
      if (token && !headers["Authorization"]) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ detail: response.statusText }));

      // Extract error message - handle both string and object detail formats
      let errorMessage: string;
      if (typeof error.detail === "string") {
        errorMessage = error.detail;
      } else if (error.detail && typeof error.detail === "object") {
        // Backend may return { code, message, ... } object
        errorMessage =
          error.detail.message ||
          error.detail.code ||
          JSON.stringify(error.detail);
      } else {
        errorMessage = `HTTP ${response.status}`;
      }

      logger.error("api_error", {
        status: response.status,
        endpoint,
        error: errorMessage,
      });

      // Handle 401 Unauthorized - clear auth state and notify user
      if (response.status === 401 && this.onUnauthorized) {
        this.onUnauthorized();
      }

      throw new Error(errorMessage);
    }

    const data = await response.json();
    logger.debug("api_response", { endpoint });
    return data;
  }

  /**
   * Make a request with automatic retry for transient failures.
   *
   * @param endpoint - API endpoint
   * @param options - Fetch options
   * @param retryConfig - Retry configuration (uses defaults if not specified)
   * @returns Promise with response data
   */
  private async requestWithRetry<T>(
    endpoint: string,
    options?: RequestInit & { skipAuth?: boolean },
    retryConfig: RetryConfig = DEFAULT_RETRY_CONFIG,
  ): Promise<T> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= retryConfig.maxRetries; attempt++) {
      try {
        return await this.request<T>(endpoint, options);
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        // Extract status from error message if available (format: "HTTP XXX" or error detail)
        const statusMatch = lastError.message.match(/HTTP (\d+)/);
        const status = statusMatch ? parseInt(statusMatch[1], 10) : 0;

        // Don't retry non-retryable errors or if we've exhausted attempts
        const isRetryable =
          retryConfig.retryableStatuses.includes(status) || status === 0;
        const hasMoreAttempts = attempt < retryConfig.maxRetries;

        if (!isRetryable || !hasMoreAttempts) {
          throw lastError;
        }

        // Calculate delay with exponential backoff
        const delay = Math.min(
          retryConfig.baseDelay * 2 ** attempt,
          retryConfig.maxDelay,
        );
        logger.warn("api_retry", {
          endpoint,
          attempt: attempt + 1,
          maxRetries: retryConfig.maxRetries,
          delay,
          error: lastError.message,
        });

        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    // Should never reach here, but TypeScript needs this
    throw lastError || new Error("Request failed");
  }

  // Health check (uses retry for transient failures)
  async ping() {
    return this.requestWithRetry<{
      status: string;
      message: string;
      loaded_questions: number;
    }>("/ping");
  }

  // Room management
  async createRoom(data: CreateRoomRequest): Promise<CreateRoomResponse> {
    return this.request<CreateRoomResponse>("/rooms", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async joinRoom(data: JoinRoomRequest): Promise<JoinRoomResponse> {
    return this.request<JoinRoomResponse>(`/rooms/${data.session_id}/join`, {
      method: "POST",
      body: JSON.stringify({ display_name: data.display_name }),
    });
  }

  async getRoomDetails(sessionId: string) {
    return this.request(`/rooms/${sessionId}`);
  }

  async listRooms(): Promise<RoomListResponse> {
    return this.requestWithRetry<RoomListResponse>("/rooms/list");
  }

  async leaveRoom(
    sessionId: string,
    playerId: string,
  ): Promise<{ status: string; message: string }> {
    return this.request(`/rooms/${sessionId}/players/${playerId}`, {
      method: "DELETE",
    });
  }

  async toggleRoomPrivacy(
    sessionId: string,
    isPublic: boolean,
  ): Promise<{ status: string; is_public: boolean; message: string }> {
    return this.request(`/rooms/${sessionId}/privacy`, {
      method: "PATCH",
      body: JSON.stringify({ is_public: isPublic }),
    });
  }

  async lookupRoomByCode(roomCode: string): Promise<RoomLookupResponse> {
    return this.request<RoomLookupResponse>(`/magic/${roomCode}`);
  }

  // NPC Management
  async listNpcPersonas(): Promise<Array<Record<string, unknown>>> {
    return this.request<Array<Record<string, unknown>>>("/npcs/personas");
  }

  async addNpcsToRoom(
    sessionId: string,
    npcs: { persona_id: string; allow_chat: boolean }[],
  ): Promise<{
    status: string;
    message: string;
    added_npcs: {
      player_id: string;
      display_name: string;
      claim_code: string;
    }[];
  }> {
    return this.request(`/rooms/${sessionId}/add-npcs`, {
      method: "POST",
      body: JSON.stringify({ npcs }),
    });
  }

  async claimNpc(
    sessionId: string,
    claimCode: string,
  ): Promise<ClaimNpcResponse> {
    return this.request<ClaimNpcResponse>(`/rooms/${sessionId}/claim-npc`, {
      method: "POST",
      body: JSON.stringify({ claim_code: claimCode }),
    });
  }

  // Active Session Management (single-game-per-user)
  async getMySession(): Promise<MySessionResponse> {
    return this.request<MySessionResponse>("/rooms/my-session");
  }

  async leaveMySession(): Promise<LeaveSessionResponse> {
    return this.request<LeaveSessionResponse>("/rooms/my-session", {
      method: "DELETE",
    });
  }

  // Cards
  async listCards(): Promise<CardsListResponse> {
    return this.request<CardsListResponse>("/cards/list");
  }

  // games (uses retry for transient failures)
  async listGames(): Promise<GamesResponse> {
    return this.requestWithRetry<GamesResponse>("/games");
  }

  // Alias for backward compatibility with RoomList
  async listGameModes(): Promise<GamesResponse> {
    return this.listGames();
  }

  // Admin endpoints (now use JWT Bearer token automatically)
  async adminCheckAccess(): Promise<{
    status: string;
    user_id: string;
    username: string;
    permissions: string[];
  }> {
    return this.request("/admin/check");
  }

  async adminGetStats(): Promise<AdminStatsResponse> {
    return this.request<AdminStatsResponse>("/admin/stats");
  }

  async adminListSessions(): Promise<AdminSessionsResponse> {
    return this.request<AdminSessionsResponse>("/admin/sessions");
  }

  async adminGetSessionDetail(
    sessionId: string,
  ): Promise<AdminSessionDetailResponse> {
    return this.request<AdminSessionDetailResponse>(
      `/admin/sessions/${sessionId}`,
    );
  }

  async adminDeleteSession(sessionId: string): Promise<AdminDeleteResponse> {
    return this.request<AdminDeleteResponse>(`/admin/sessions/${sessionId}`, {
      method: "DELETE",
    });
  }

  async adminFlushRedis(): Promise<AdminFlushResponse> {
    return this.request<AdminFlushResponse>("/admin/redis/flush", {
      method: "POST",
    });
  }

  async adminBroadcast(
    sessionId: string,
    message: string,
  ): Promise<AdminBroadcastResponse> {
    return this.request<AdminBroadcastResponse>(
      `/admin/sessions/${sessionId}/broadcast?message=${encodeURIComponent(message)}`,
      {
        method: "POST",
      },
    );
  }

  async adminGetUsers(): Promise<AdminUsersResponse> {
    return this.request<AdminUsersResponse>("/admin/users");
  }

  async adminGetRecentlyActiveUsers(
    limit: number = 10,
  ): Promise<AdminUsersResponse> {
    return this.request<AdminUsersResponse>(
      `/admin/users/recent?limit=${limit}`,
    );
  }

  async adminUpdateUser(
    userId: string,
    data: AdminUserUpdateRequest,
  ): Promise<AdminUserInfo> {
    return this.request<AdminUserInfo>(`/admin/users/${userId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async adminResetUserPassword(
    userId: string,
  ): Promise<AdminPasswordResetResponse> {
    return this.request<AdminPasswordResetResponse>(
      `/admin/users/${userId}/reset-password`,
      {
        method: "POST",
      },
    );
  }

  async adminDisableUser(userId: string): Promise<AdminUserInfo> {
    return this.request<AdminUserInfo>(`/admin/users/${userId}/disable`, {
      method: "POST",
    });
  }

  async adminEnableUser(userId: string): Promise<AdminUserInfo> {
    return this.request<AdminUserInfo>(`/admin/users/${userId}/enable`, {
      method: "POST",
    });
  }

  async adminDeleteUser(userId: string): Promise<AdminUserDeleteResponse> {
    return this.request<AdminUserDeleteResponse>(`/admin/users/${userId}`, {
      method: "DELETE",
    });
  }

  async adminGetUserHistory(
    userId: string,
    offset = 0,
    limit = 10,
  ): Promise<AdminUserAuditHistoryResponse> {
    return this.request<AdminUserAuditHistoryResponse>(
      `/admin/users/${userId}/history?offset=${offset}&limit=${limit}`,
    );
  }

  // Admin invite code endpoints
  async adminListInviteCodes(): Promise<AdminInviteListResponse> {
    return this.request<AdminInviteListResponse>("/admin/invites");
  }

  async adminCreateInviteCode(
    data: AdminCreateInviteRequest,
  ): Promise<AdminCreateInviteResponse> {
    return this.request<AdminCreateInviteResponse>("/admin/invites", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async adminRevokeInviteCode(
    inviteId: string,
  ): Promise<AdminRevokeInviteResponse> {
    return this.request<AdminRevokeInviteResponse>(
      `/admin/invites/${inviteId}`,
      {
        method: "DELETE",
      },
    );
  }

  async adminGetInviteUsage(
    inviteId: string,
  ): Promise<AdminInviteUsageResponse> {
    return this.request<AdminInviteUsageResponse>(
      `/admin/invites/${inviteId}/usage`,
    );
  }

  // Authentication endpoints
  async register(data: RegisterRequest): Promise<RegisterResponse> {
    return this.request<RegisterResponse>("/auth/register", {
      method: "POST",
      body: JSON.stringify(data),
      skipAuth: true, // Don't try to inject token for registration
    });
  }

  async login(data: LoginRequest): Promise<LoginResponse> {
    return this.request<LoginResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify(data),
      skipAuth: true, // Don't try to inject token for login
    });
  }

  async refreshToken(data: TokenRefreshRequest): Promise<TokenRefreshResponse> {
    return this.request<TokenRefreshResponse>("/auth/refresh", {
      method: "POST",
      body: JSON.stringify(data),
      skipAuth: true, // Use refresh token, not access token
    });
  }

  async getProfile(): Promise<UserProfileResponse> {
    return this.request<UserProfileResponse>("/auth/me");
  }

  async logout(): Promise<{ message: string }> {
    return this.request<{ message: string }>("/auth/logout", {
      method: "POST",
    });
  }

  async updateTheme(
    themeId: string,
    themePrimaryColor?: string | null,
  ): Promise<UserProfileResponse> {
    return this.request<UserProfileResponse>("/auth/me/theme", {
      method: "PATCH",
      body: JSON.stringify({
        theme_id: themeId,
        theme_primary_color: themePrimaryColor,
      }),
    });
  }

  async updateProfileEmoji(emoji: string): Promise<UserProfileResponse> {
    return this.request<UserProfileResponse>("/auth/me/emoji", {
      method: "PATCH",
      body: JSON.stringify({ emoji }),
    });
  }

  // Factory endpoints
  async factoryListGames(): Promise<FactoryGame[]> {
    return this.request<FactoryGame[]>("/factory/games");
  }

  async factoryGetSchema(gameId: string): Promise<ContentSchema> {
    return this.request<ContentSchema>(`/factory/schemas/${gameId}`);
  }

  async factoryListCollections(gameId: string): Promise<string[]> {
    return this.request<string[]>(`/factory/collections/${gameId}`);
  }

  async factoryLoadCollection(
    gameId: string,
    collectionId: string,
  ): Promise<ContentCollection> {
    return this.request<ContentCollection>(
      `/factory/collections/${gameId}/${collectionId}`,
    );
  }

  async factorySaveCollection(
    gameId: string,
    collectionId: string,
    collection: ContentCollection,
  ): Promise<SaveCollectionResponse> {
    return this.request<SaveCollectionResponse>(
      `/factory/collections/${gameId}/${collectionId}`,
      {
        method: "POST",
        body: JSON.stringify(collection),
      },
    );
  }

  async factoryValidateCollection(
    collection: ContentCollection,
  ): Promise<ValidationReport> {
    return this.request<ValidationReport>("/factory/validate", {
      method: "POST",
      body: JSON.stringify(collection),
    });
  }

  async factoryDeleteItem(
    gameId: string,
    collectionId: string,
    itemId: string,
  ): Promise<{ status: string }> {
    return this.request<{ status: string }>(
      `/factory/collections/${gameId}/${collectionId}/item/${itemId}`,
      {
        method: "DELETE",
      },
    );
  }

  // Game Settings & Presets
  async fetchGamePresets(
    gameId: string,
  ): Promise<import("../types/game").GamePreset[]> {
    const response = await this.request<{
      presets: import("../types/game").GamePreset[];
      total: number;
    }>(`/games/${gameId}/presets`);
    return response.presets;
  }

  async fetchGameConfigSchema(
    gameId: string,
  ): Promise<import("../types/game").GameConfigSchema> {
    const response = await this.request<{
      constraints: import("../types/game").GameConfigSchema;
    }>(`/games/${gameId}/config/schema`);
    return response.constraints;
  }

  // Test Mode endpoints
  async getTestmodeStatus(): Promise<TestmodeStatusResponse> {
    return this.request<TestmodeStatusResponse>("/testmode/status");
  }

  async getModeSettings(): Promise<ModeSettingsResponse> {
    return this.request<ModeSettingsResponse>("/testmode/admin/modes");
  }

  async toggleTestmode(enabled: boolean): Promise<ModeToggleResponse> {
    return this.request<ModeToggleResponse>("/testmode/admin/modes/testmode", {
      method: "POST",
      body: JSON.stringify({ enabled }),
    });
  }

  async toggleDevmode(enabled: boolean): Promise<ModeToggleResponse> {
    return this.request<ModeToggleResponse>("/testmode/admin/modes/devmode", {
      method: "POST",
      body: JSON.stringify({ enabled }),
    });
  }

  async toggleAutoLogin(enabled: boolean): Promise<ModeToggleResponse> {
    return this.request<ModeToggleResponse>("/testmode/admin/modes/autologin", {
      method: "POST",
      body: JSON.stringify({ enabled }),
    });
  }
}

// Export singleton instance
export const api = new ApiClient();

// Export class for testing
export { ApiClient };
